import React from 'react'
import PropTypes from 'prop-types'
import { Box, ErrorBoundary, MediaQuery, Placeholder, Level, Text, Grid, Rating, Title, Icon } from '@deseretbook/react-ui'
import { useMediaQuery } from '@deseretbook/react-utils'
import { Logger } from '../../../dbcom'
import { DeseretBook } from '../../../dbcom/api'
import {
  ReviewsFilter,
  ReviewsOverview,
  ReviewsList,
  useReviewFilter,
  MostHelpful,
  StarOnlyRating,
} from '.'

const logger = new Logger({ context: 'product/reviews/ReviewsSection' })

/**
 * Contains all the different review formats and product reviews
 * to be used on product pages
 * @returns {function} Component
 */
export const ReviewsSection = ({ product, verifiedPurchaser = false }) => {
  const [isLoadingReviews, setLoadingReviews] = React.useState(true)

  const [productReviews, setProductReviews] = React.useState()
  const [filteredReviews, setFilters, filters] = useReviewFilter(
    !isLoadingReviews && productReviews && productReviews.reviews,
  )

  // For when the content reaches its mobile breakpoint
  const isMobile = useMediaQuery({ maxWidth: 639 })
  // For when content reaches mobile widths but isn't technically mobile
  const isNarrow = useMediaQuery({ minWidth: 768, maxWidth: 994 })

  /**
   * Gets the Products reviews
   */
  const getReviews = async () => {
    try {
      const productReviewsResponse = await DeseretBook.product({
        id: product.id,
      })
        .review()
        .all()

      setProductReviews(productReviewsResponse)
      setLoadingReviews(false)
    } catch (message) {
      logger.error(`Unable to fetch product reviews: "${message}"`)
    }
  }

  // Fetch data on initial load
  React.useEffect(() => {

    if (isLoadingReviews) {
      getReviews()
    }

  },  [isLoadingReviews])

  return (
    <React.Fragment>
      <Box id="reviews" maxWidth={32} paddingTop={1.25}>
        <ErrorBoundary failsSilently>
          {!!(isLoadingReviews || productReviews) &&
            (isLoadingReviews ? (
              <MediaQuery.Medium>
                {isMediumOrLarger => (
                  <Placeholder>
                    <Level>
                      <Level.Left>
                        <Level.Item>
                          <Box
                            {...(isMediumOrLarger
                              ? {
                                  width: 14,
                                  alignItems: 'center',
                                  isColumn: true,
                                }
                              : {
                                  minWidth: 6,
                                  maxWidth: 14,
                                  isFullwidth: true,
                                })}
                          >
                            <Box
                              paddingBotton={1}
                              alignItems="center"
                              isColumn={!isMediumOrLarger}
                            >
                              <Text color="disabled" isBold size="xLarge">
                                0.0
                              </Text>
                              <Box
                                isColumn
                                alignItems={
                                  isMediumOrLarger ? 'start' : 'center'
                                }
                                marginLeft={isMediumOrLarger && 0.5}
                              >
                                {isMediumOrLarger && <Rating average={5} />}
                                <Text color="disabled" size="small">
                                  0 Reviews
                                </Text>
                              </Box>
                            </Box>
                            {isMediumOrLarger && (
                              <Box
                                height={2.5}
                                width={9.5}
                                marginHorizontal={0.25}
                              />
                            )}
                          </Box>
                        </Level.Item>
                      </Level.Left>
                      <Level.Right isShrinkable>
                        <Level.Item>
                          <Box flexGrow={1} width={20}>
                            {[5, 4, 3, 2, 1].map(starRating => (
                              <Level key={starRating}>
                                <Level.Left>
                                  <Level.Item>
                                    <Box width={3}>
                                      <Rating
                                        average={starRating}
                                        type="minimal"
                                      />
                                    </Box>
                                  </Level.Item>
                                </Level.Left>
                                <Level.Item>
                                  <Placeholder.Block
                                    width="100%"
                                    height={0.25}
                                  />
                                </Level.Item>
                                <Level.Right>
                                  <Level.Item>
                                    <Box width={2}>
                                      <Text
                                        color="disabled"
                                        hasEllipsis
                                        alignment="right"
                                      >
                                        0
                                      </Text>
                                    </Box>
                                  </Level.Item>
                                </Level.Right>
                              </Level>
                            ))}
                          </Box>
                        </Level.Item>
                      </Level.Right>
                    </Level>
                    <Box marginTop={2}>
                      <Placeholder.Block />
                    </Box>
                  </Placeholder>
                )}
              </MediaQuery.Medium>
            ) : (
              <ReviewsOverview
                productId={product.id}
                reviews={productReviews.reviews}
                avgRating={productReviews.avgRating}
                setFilters={setFilters}
              />
            ))}
        </ErrorBoundary>
      </Box>

    {/* Star only rating */}
    <ErrorBoundary failsSilently>
      {!!(isLoadingReviews || productReviews) &&
        (isLoadingReviews  ? (
          <Placeholder>
            <Box
              marginVertical={2}
              padding={1}
              maxWidth={isMobile ? '100%' : 45}
            >
              <Grid
                alignItems="center"
                justifyItems="left"
                breakpoints={{
                  '100': {
                    areas: ['title', 'rating', 'text', 'link'],
                    columns: Grid.fr(1),
                    rows: [
                      2,
                      4,
                      Grid.fr(1),
                      Grid.minMax(Grid.minContent, Grid.maxContent),
                    ],
                    justifyItems: 'center',
                  },
                  '640': {
                    areas: ['title rating link', 'text text text'],
                    columns: [
                      Grid.minMax(Grid.minContent, Grid.maxContent),
                      Grid.fr(1),
                      Grid.minMax(Grid.minContent, Grid.maxContent),
                    ],
                    columnGap: 1,
                    rows: [3, Grid.fr(1)],
                    justifyItems: 'left',
                  },
                  '768': {
                    areas: ['title', 'rating', 'text', 'link'],
                    columns: Grid.fr(1),
                    rows: [
                      2,
                      4,
                      Grid.fr(1),
                      Grid.minMax(Grid.minContent, Grid.maxContent),
                    ],
                    justifyItems: 'center',
                  },
                  '995': {
                    areas: ['title rating link', 'text text text'],
                    columns: [
                      Grid.minMax(Grid.minContent, Grid.maxContent),
                      Grid.fr(1),
                      Grid.minMax(Grid.minContent, Grid.maxContent),
                    ],
                    columnGap: 1,
                    rows: [3, Grid.fr(1)],
                    justifyItems: 'left',
                  },
                }}
              >
                <Grid.Item area="title">
                  <Placeholder.Block
                    height={1.5}
                    width={10}
                    alignItems="center"
                  />
                </Grid.Item>
                <Grid.Item area="rating">
                  <Box alignItems="center">
                    <Rating
                      size={isMobile ? 'large' : 'medium'}
                      average={5}
                    />
                  </Box>
                </Grid.Item>
                <Grid.Item
                  area="link"
                  justify={isMobile || isNarrow ? 'center' : 'right'}
                >
                  <Placeholder.Block height={2} width={6} />
                </Grid.Item>
                <Grid.Item area="text">
                  <Box marginVertical={0.5}>
                    <Placeholder.Block height={1} width={20} />
                    <Placeholder.Block
                      marginTop={1}
                      height={1}
                      width={10}
                    />
                  </Box>
                </Grid.Item>
              </Grid>
            </Box>
          </Placeholder>
        ) : (
          <StarOnlyRating
            productId={product.id}
            productName={product.name}
            verifiedPurchaser={verifiedPurchaser}
            isMobile={isMobile}
            isNarrow={isNarrow}
          />
        ))}
    </ErrorBoundary>

    {/* Most Helpful Positive/Negative Reviews */}
    <ErrorBoundary failsSilently>
      {!!(isLoadingReviews || productReviews) &&
        (isLoadingReviews  ? (
          <Placeholder>
            {[5, 1].map(rating => (
              <Box marginVertical={1.5}>
                <Level>
                  <Level.Left>
                    <Level.Item>
                      <Icon
                        name={rating === 5 ? 'thumbUp' : 'thumbDown'}
                        shade="light"
                        color="secondary"
                      />
                    </Level.Item>
                    <Level.Item>
                      <Title
                        title={`Most helpful ${
                          rating === 5 ? 'positive' : 'critical'
                        } review`}
                        titleAsComponent="h3"
                        subtitle="Customers found this helpful"
                      />
                    </Level.Item>
                  </Level.Left>
                </Level>
                <Box>
                  <Rating average={rating} size="small" />
                  <Placeholder.Block
                    isInline
                    marginLeft={0.5}
                    width={10}
                    height={1}
                  />
                  <Placeholder.Block
                    width={12}
                    height={1}
                    marginBottom={1}
                    marginTop={0.5}
                  />
                  <Placeholder.Text lines={3} />
                </Box>
              </Box>
            ))}
          </Placeholder>
        ) : (
          <Box marginVertical={2}>
            <MostHelpful reviews={productReviews.reviews} />
          </Box>
        ))}
    </ErrorBoundary>

    {/* Reviews Filter */}
    <ErrorBoundary failsSilently>
      {!!(isLoadingReviews || productReviews) &&
        (isLoadingReviews ? (
          <div />
        ) : (
          <Box marginTop={1} maxWidth={32}>
            <ReviewsFilter setFilters={setFilters} filters={filters} />
          </Box>
        ))}
    </ErrorBoundary>

    {/* Reviews List */}
    {!!(isLoadingReviews || productReviews) && (
      <Box paddingVertical={2}>
        {isLoadingReviews  ? (
          <Placeholder>
            {[1, 2, 3].map(num => (
              <Box key={num} marginBottom={1}>
                <Rating average="5" size="small" />
                <Placeholder.Block
                  isInline
                  marginLeft={0.5}
                  width={10}
                  height={1}
                />
                <Placeholder.Block
                  width={12}
                  height={1}
                  marginBottom={1}
                  marginTop={0.5}
                />
                <Placeholder.Text lines={3} />
              </Box>
            ))}
          </Placeholder>
        ) : (
          <ReviewsList
            filters={filters}
            setFilters={setFilters}
            total={productReviews.reviews.length}
            reviews={filteredReviews}
            productId={product.id}
          />
        )}
      </Box>
    )}
    </React.Fragment>
  )
}

ReviewsSection.propTypes = {
  product: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.number,
  }),
  verifiedPurchaser: PropTypes.bool,
}